import { formatMoney } from 'accounting';
import { monthName } from 'common/helpers';
import { IconButton } from 'components/action';
import { SavingIndicator, SavingIndicatorStatus } from 'components/indicator';
import { action, computed, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as moment from 'moment';
import * as React from 'react';
import { IAppOnlyStore } from 'stores/AllStores';
import {
  IMonthlyCollection,
  IMonthlyCollectionDetails,
  CollectionsFetchStatus,
} from 'stores/OrderPadSales';

// Styles
const s = require('./RepCollectionsPeriod.css');
const expandIcon = require('assets/dropdown arrow.svg');
const collapseIcon = require('assets/close blue icon.svg');

// Props
interface IRepCollectionsPeriodProps extends IAppOnlyStore {
  period: IMonthlyCollection;
}

// Component
@inject('store')
@observer
export class RepCollectionsPeriod extends React.Component<
  IRepCollectionsPeriodProps,
  {}
> {
  @observable
  private isExpanded: boolean = false;

  @computed
  private get data(): IMonthlyCollectionDetails | null {
    const {
      orderPadSales: { collectionsDetailedData, collectionsDetailedDataKeys },
    } = this.props.store;
    const index = collectionsDetailedDataKeys.indexOf(this.props.period.key);

    if (index === -1) {
      return null;
    }

    return collectionsDetailedData[index];
  }

  @computed
  private get isLoading(): boolean {
    return (
      this.data === null ||
      this.data.fetchStatus === CollectionsFetchStatus.Loading
    );
  }

  @computed
  private get isLoadingForTheFirstTime(): boolean {
    return (
      this.isLoading &&
      (this.data === null || this.data.fetchLastTimestamp === 0)
    );
  }

  @computed
  private get isError(): boolean {
    return (
      this.data !== null &&
      this.data.fetchStatus === CollectionsFetchStatus.Error
    );
  }

  public render() {
    const { period } = this.props;

    return (
      <div className={s.period}>
        <div className={s.summary} onClick={this.handleSummaryRowClick}>
          <p className={s.label}>
            <strong>{monthName(period.month)}</strong>
            {' ' + period.year}
          </p>

          <SavingIndicator
            status={
              this.isLoading && !this.isLoadingForTheFirstTime
                ? SavingIndicatorStatus.Saving
                : SavingIndicatorStatus.None
            }
            labels={{
              saving: 'Refreshing',
            }}
          />

          <div className={s.right}>
            <p className={s.amount}>{formatMoney(period.amount)}</p>

            <IconButton
              onClick={this.handleButtonClick}
              removePadding={true}
              iconClassName={this.isExpanded ? s.collapseIcon : s.expandIcon}
              icon={this.isExpanded ? collapseIcon : expandIcon}
            />
          </div>
        </div>

        {(this.isExpanded && period.amount <= 0 && (
          <div className={s.details}>
            <div className={s.noResults}>
              <p>No collections available for this month.</p>
            </div>
          </div>
        )) ||
          (this.isExpanded && (
            <div className={s.details}>
              {(this.isLoadingForTheFirstTime || this.isError) && (
                <div className={s.loading}>
                  <SavingIndicator
                    onRetry={this.handleFetchRetry}
                    align="right"
                    status={
                      this.isError
                        ? SavingIndicatorStatus.Error
                        : SavingIndicatorStatus.Saving
                    }
                    labels={{
                      saving: 'Loading',
                      error: 'Could not fetch data.',
                      retry: ' Click here to try again.',
                    }}
                  />
                </div>
              )}

              {this.data && (
                <table className={s.data}>
                  <thead>
                    <tr>
                      <th>Pay ID</th>
                      <th>Date</th>
                      <th>Card Code</th>
                      <th>Card Name</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {this.data.payments.map((payment) => (
                      <tr>
                        <td>{payment.docnum}</td>
                        <td>{moment(payment.docdate).format('M/DD/YYYY')}</td>
                        <td>{payment.cardcode}</td>
                        <td>{payment.cardname}</td>
                        <td>{formatMoney(payment.doctotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
      </div>
    );
  }

  private handleSummaryRowClick = (e: React.MouseEvent<HTMLDivElement>) => {
    this.toggleExpansion();
  };

  private handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  private handleFetchRetry = () => {
    this.props.store.orderPadSales.fetchDetailedCollectionsForMonth(
      this.props.period.key
    );
  };

  @action
  private toggleExpansion = () => {
    const toExpand = !this.isExpanded;
    this.isExpanded = toExpand;

    if (toExpand) {
      this.props.store.orderPadSales.fetchDetailedCollectionsForMonth(
        this.props.period.key
      );
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/routes/sales/Lead/Sales/RepCollectionsPeriod.tsx